/*
  MAM Forni "Build your oven" — shared builder engine (2026-10-03).

  Used by:
    - snippets/fwo-mam-builder.liquid (product page; window.fwoMamBuilder)
    - assets/quote-form.js (pop-up when a MAM product is picked in the quote
      form's product search, so a request can't be sent without a build)

  Data comes from snippets/fwo-mam-builder-data.liquid (inline on the product
  page, or via /products/<handle>?view=mambuilder for the quote form):
    { config: {axes, images}, units: [{id,title,qty,media}], special: <variant id>,
      media: [{id, file, src, srcset}] }
  `config` is the product's custom.mam_builder metafield — see
  firewood-oven-theme/CLAUDE.md § MAM "Build your oven".

  FwoMamBuilder.create(rootEl, data, { showMedia(mediaObj) }) renders into rootEl
  (which must contain FwoMamBuilder.shellHtml()) and returns
  { missing(), showMissing(list, scroll), spec(), variantId() }.

  The two themes keep their own copy of this file; ONLY the THEME block
  (markup classes + CSS) differs. Keep the rest identical.
*/
/* FWO i18n: T() shows Spanish on /es (snippets/fwo-i18n.liquid); spec/cart text stays English. */
(function () {
  var T = function (s) { return window.FwoT ? window.FwoT(s) : s; };
  if (window.FwoMamBuilder) return;

  // ---- THEME block (Concept) ----
  var THEME = {
    group: 'fwo-config',              // fieldset per axis
    legend: 'fwo-config__label',
    options: 'fwo-config__group',     // pill row
    selected: 'is-selected',
    pillText: 'fwo-config__pill',
    optionalStyle: 'opacity:.6',
    // Pill styles mirror snippets/fwo-color-picker.liquid (which only prints them
    // when the color picker renders), scoped under .fwo-mam.
    css: [
      '.fwo-mam { margin: 4px 0 24px; }',
      '.fwo-mam__head { font-weight: 700; font-size: 18px; margin: 0 0 6px; }',
      '.fwo-mam__sub { font-size: 14px; opacity: .75; margin: 0 0 20px; line-height: 1.45; }',
      '.fwo-mam .fwo-config { border: 0; margin: 0 0 20px; padding: 0; }',
      '.fwo-mam .fwo-config__label { font-size: 14px; font-weight: 400; line-height: 1.2; margin-bottom: 12px; padding: 0; }',
      '.fwo-mam .fwo-config__group { display: flex; flex-wrap: wrap; gap: 12px; }',
      '.fwo-mam .fwo-config__option { position: relative; cursor: pointer; }',
      '.fwo-mam .fwo-config__radio { position: absolute !important; width: 1px !important; height: 1px !important; margin: -1px !important; padding: 0 !important; overflow: hidden; clip: rect(0, 0, 0, 0); border: 0; }',
      '.fwo-mam .fwo-config__pill { display: inline-flex; align-items: center; justify-content: center; min-width: 44px; padding: 14px 18px; border: 1px solid rgb(var(--color-foreground, 28 24 20) / 0.2); border-radius: 5px; font-size: 14px; font-weight: 500; line-height: 1.2; transition: border-color 0.15s, box-shadow 0.15s; }',
      '.fwo-mam .fwo-config__option:hover .fwo-config__pill { border-color: rgb(var(--color-foreground, 28 24 20) / 0.5); }',
      '.fwo-mam .fwo-config__option.is-selected .fwo-config__pill { border-color: #A52525; box-shadow: inset 0 0 0 1px #A52525; }',
      '.fwo-mam .fwo-config__option[aria-disabled="true"] { opacity: .35; cursor: not-allowed; }',
      // Long labels (an in-stock unit's full spec) wrap inside the pill instead of running off the page.
      '.fwo-mam .fwo-config__option { max-width: 100%; }',
      '.fwo-mam .fwo-config__pill { max-width: 100%; white-space: normal; text-align: left; overflow-wrap: anywhere; }',
      '.fwo-mam__desc { font-size: 13px; opacity: .75; margin-top: 8px; min-height: 1em; white-space: pre-line; }',
      '.fwo-mam__text { width: 100%; max-width: 420px; padding: 12px 14px; border: 1px solid rgb(var(--color-foreground, 28 24 20) / 0.2); border-radius: 5px; font: inherit; font-size: 14px; }',
      '.fwo-mam__photo-note, .fwo-mam__summary { font-size: 13px; background: rgb(var(--color-foreground, 28 24 20) / 0.04); border-radius: 5px; padding: 10px 12px; margin: 0 0 14px; line-height: 1.5; }',
      '.fwo-mam__missing { color: #A52525; font-weight: 600; font-size: 14px; margin: 6px 0 0; }',
      '.fwo-mam__qty { font-weight: 400; opacity: .7; margin-left: 6px; }',
      // AVPN preset button (2026-10-20)
      '.fwo-mam__avpn { margin: 0 0 20px; }',
      '.fwo-mam__avpn-btn { display: inline-flex; align-items: center; gap: 10px; padding: 10px 16px 10px 10px; border: 1px solid #A52525; border-radius: 999px; background: #fff; color: #A52525; font: inherit; font-size: 14px; font-weight: 600; cursor: pointer; transition: background .15s ease, color .15s ease; }',
      '.fwo-mam__avpn-btn:hover { background: #A52525; color: #fff; }',
      '.fwo-mam__avpn-btn img { width: 26px; height: auto; display: block; background: #fff; border-radius: 50%; }',
      '.fwo-mam__avpn-btn.is-on { background: #A52525; color: #fff; }',
      '.fwo-mam__avpn .fwo-mam__desc { margin-top: 6px; }',
      // RAL Classic picker under a text axis with "ral": true (2026-10-21)
      '.fwo-ral { margin-top: 10px; max-width: 520px; }',
      '.fwo-ral__toggle { display: inline-flex; align-items: center; gap: 10px; padding: 8px 14px 8px 8px; border: 1px solid rgb(var(--color-foreground, 28 24 20) / 0.2); border-radius: 999px; background: transparent; font: inherit; font-size: 14px; cursor: pointer; color: inherit; }',
      '.fwo-ral__toggle:hover { border-color: rgb(var(--color-foreground, 28 24 20) / 0.5); }',
      '.fwo-ral__chip { width: 24px; height: 24px; border-radius: 50%; box-shadow: inset 0 0 0 1px rgb(0 0 0 / .15); background: conic-gradient(#AB1519, #E6BE05, #3A8258, #0F518A, #852E6F, #AB1519); flex-shrink: 0; }',
      '.fwo-ral__panel { margin-top: 10px; padding: 12px; border: 1px solid rgb(var(--color-foreground, 28 24 20) / 0.15); border-radius: 10px; background: rgb(var(--color-background, 255 255 255)); }',
      '.fwo-ral__search { width: 100%; padding: 10px 12px; border: 1px solid rgb(var(--color-foreground, 28 24 20) / 0.2); border-radius: 6px; font: inherit; font-size: 14px; }',
      '.fwo-ral__fams { display: flex; flex-wrap: wrap; gap: 6px; margin: 10px 0; }',
      '.fwo-ral__fam { display: inline-flex; align-items: center; gap: 6px; padding: 5px 10px; border: 1px solid rgb(var(--color-foreground, 28 24 20) / 0.15); border-radius: 999px; background: transparent; font: inherit; font-size: 12px; cursor: pointer; color: inherit; }',
      '.fwo-ral__fam i { width: 12px; height: 12px; border-radius: 50%; display: inline-block; box-shadow: inset 0 0 0 1px rgb(0 0 0 / .15); }',
      '.fwo-ral__fam.is-on { border-color: #A52525; box-shadow: inset 0 0 0 1px #A52525; }',
      '.fwo-ral__grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(84px, 1fr)); gap: 6px; max-height: 300px; overflow-y: auto; padding: 2px; }',
      '.fwo-ral__sw { display: flex; flex-direction: column; justify-content: flex-end; min-height: 64px; padding: 6px; border: 0; border-radius: 6px; font: inherit; font-size: 11px; line-height: 1.2; text-align: left; cursor: pointer; box-shadow: inset 0 0 0 1px rgb(0 0 0 / .12); }',
      '.fwo-ral__sw b { font-size: 12px; }',
      '.fwo-ral__sw:hover, .fwo-ral__sw:focus-visible { outline: 2px solid #A52525; outline-offset: 1px; }',
      '.fwo-ral__sw.is-on { outline: 3px solid #A52525; outline-offset: 1px; }',
      '.fwo-ral__note, .fwo-ral__empty { font-size: 12px; opacity: .7; margin: 8px 0 0; }'
    ].join('\n')
  };
  // ---- end THEME block ----

  function ensureStyles() {
    if (document.getElementById('fwo-mam-builder-css')) return;
    var st = document.createElement('style');
    st.id = 'fwo-mam-builder-css';
    st.textContent = THEME.css;
    document.head.appendChild(st);
  }

  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }

  // RAL Classic, all 216 colours: [code, English name, hex] (2026-10-21, from ralcolorchart.com/ral-classic).
  // Screen colours are approximations — the code is what gets painted.
  var RAL = [["1000","Green beige","C5BB8A"],["1001","Beige","C6B286"],["1002","Sand yellow","C7AE72"],["1003","Signal yellow","E6B019"],["1004","Golden yellow","D2A40E"],["1005","Honey yellow","BC9611"],["1006","Maize yellow","CF9804"],["1007","Daffodil yellow","D49300"],["1011","Brown beige","A38454"],["1012","Lemon yellow","CFB539"],["1013","Oyster white","DFDBC7"],["1014","Ivory","D4C79C"],["1015","Light ivory","DED3B6"],["1016","Sulfur yellow","E8E253"],["1017","Saffron yellow","E4AF56"],["1018","Zinc yellow","EBD346"],["1019","Grey beige","9C917B"],["1020","Olive yellow","999167"],["1021","Colza yellow","E5C000"],["1023","Traffic yellow","E6BE05"],["1024","Ochre yellow","AD9451"],["1026","Luminous yellow","FFFF00"],["1027","Curry","998420"],["1028","Melon yellow","F2A500"],["1032","Broom yellow","CFA81E"],["1033","Dahlia yellow","E4A02D"],["1034","Pastel yellow","D9A156"],["1035","Pearl beige","898271"],["1036","Pearl gold","746341"],["1037","Sun yellow","DB9A17"],["2000","Yellow orange","C7750F"],["2001","Red orange","A74D23"],["2002","Vermilion","AC3721"],["2003","Pastel orange","E17C30"],["2004","Pure orange","CC5608"],["2005","Luminous orange","FF4612"],["2007","Luminous bright orange","FFAD19"],["2008","Bright red orange","D66C21"],["2009","Traffic orange","C9560D"],["2010","Signal orange","BC602D"],["2011","Deep orange","CF7421"],["2012","Salmon orange","C2674F"],["2013","Pearl orange","824128"],["2017","RAL orange","DC5B00"],["3000","Flame red","962A27"],["3001","Signal red","8F1E24"],["3002","Carmine red","8D1F24"],["3003","Ruby red","7C0D24"],["3004","Purple red","651927"],["3005","Wine red","561E27"],["3007","Black red","3D2326"],["3009","Oxide red","643730"],["3011","Brown red","6E2124"],["3012","Beige red","B7856E"],["3013","Tomato red","8A2F28"],["3014","Antique pink","BC6F72"],["3015","Light pink","CC9EA4"],["3016","Coral red","963D2F"],["3017","Rose","B9535B"],["3018","Strawberry red","B63C49"],["3020","Traffic red","AB1519"],["3022","Salmon pink","BE6954"],["3024","Luminous red","EE1729"],["3026","Luminous bright red","F71027"],["3027","Raspberry red","9E1B3C"],["3028","Pure red","B92726"],["3031","Orient red","973238"],["3032","Pearl ruby red","661925"],["3033","Pearl pink","94352D"],["4001","Red lilac","7C5B80"],["4002","Red violet","823A4B"],["4003","Heather violet","B65A88"],["4004","Claret violet","5F1837"],["4005","Blue lilac","746395"],["4006","Traffic purple","852E6F"],["4007","Purple violet","44263C"],["4008","Signal violet","7C477D"],["4009","Pastel violet","95838F"],["4010","Telemagenta","AC3B71"],["4011","Pearl violet","685C80"],["4012","Pearl blackberry","67657A"],["5000","Violet blue","35496B"],["5001","Green blue","294763"],["5002","Ultramarine blue","193278"],["5003","Sapphire blue","203151"],["5004","Black blue","1E222C"],["5005","Signal blue","134A85"],["5007","Brillant blue","466589"],["5008","Grey blue","2F3A44"],["5009","Azure blue","365875"],["5010","Gentian blue","0E457A"],["5011","Steel blue","222C3E"],["5012","Light blue","457FB3"],["5013","Cobalt blue","212F51"],["5014","Pigeon blue","667691"],["5015","Sky blue","3172AD"],["5017","Traffic blue","0F518A"],["5018","Turquoise blue","47848D"],["5019","Capri blue","265682"],["5020","Ocean blue","113E4D"],["5021","Water blue","216D76"],["5022","Night blue","282C58"],["5023","Distant blue","4D648A"],["5024","Pastel blue","6C8DAA"],["5025","Pearl gentian blue","3C6379"],["5026","Pearl night blue","1B2B4D"],["6000","Patina green","4A7363"],["6001","Emerald green","40693A"],["6002","Leaf green","3B5B2F"],["6003","Olive green","4F553E"],["6004","Blue green","214245"],["6005","Moss green","234235"],["6006","Grey olive","3C3D32"],["6007","Bottle green","2E3526"],["6008","Brown green","333327"],["6009","Fir green","2A372C"],["6010","Grass green","4E6E39"],["6011","Reseda green","6A7C5B"],["6012","Black green","2F3B39"],["6013","Reed green","777659"],["6014","Yellow olive","454339"],["6015","Black olive","3C3F38"],["6016","Turquoise green","256753"],["6017","May green","5C8144"],["6018","Yellow green","689A45"],["6019","Pastel green","B8CFAD"],["6020","Chrome green","3B4634"],["6021","Pale green","899B79"],["6022","Olive drab","3B382E"],["6024","Traffic green","3A8258"],["6025","Fern green","5D703E"],["6026","Opal green","0D5951"],["6027","Light green","88B5B3"],["6028","Pine green","3D5547"],["6029","Mint green","226C45"],["6032","Signal green","417E57"],["6033","Mint turquoise","568480"],["6034","Pastel turquoise","86A9AD"],["6035","Pearl green","2E4F31"],["6036","Pearl opal green","27514A"],["6037","Pure green","3F8C3D"],["6038","Luminous green","20A339"],["6039","Fibrous green","ABC251"],["7000","Squirrel grey","7B858D"],["7001","Silver grey","8B949B"],["7002","Olive grey","7D7965"],["7003","Moss grey","76776A"],["7004","Signal grey","969799"],["7005","Mouse grey","696D6B"],["7006","Beige grey","716C60"],["7008","Khaki grey","6C6040"],["7009","Green grey","5B6058"],["7010","Tarpaulin grey","575B57"],["7011","Iron grey","535A5E"],["7012","Basalt grey","595E60"],["7013","Brown grey","545146"],["7015","Slate grey","51535A"],["7016","Anthracite grey","3B4044"],["7021","Black grey","323537"],["7022","Umbra grey","4C4C47"],["7023","Concrete grey","7D7F76"],["7024","Graphite grey","45494E"],["7026","Granite grey","394345"],["7030","Stone grey","8C8C83"],["7031","Blue grey","5D676D"],["7032","Pebble grey","B1B1A1"],["7033","Cement grey","7C8273"],["7034","Yellow grey","8C8870"],["7035","Light grey","C2C6C3"],["7036","Platinum grey","949292"],["7037","Dusty grey","797B7B"],["7038","Agate grey","ADB0A9"],["7039","Quartz grey","68675F"],["7040","Window grey","969CA1"],["7042","Traffic grey A","8C9190"],["7043","Traffic grey B","4F5352"],["7044","Silk grey","B3B2A9"],["7045","Telegrey 1","8C9094"],["7046","Telegrey 2","7C8287"],["7047","Telegrey 4","C5C5C5"],["7048","Pearl mouse grey","7A7871"],["8000","Green brown","816D44"],["8001","Ochre brown","8F6833"],["8002","Signal brown","704F40"],["8003","Clay brown","74502F"],["8004","Copper brown","814D37"],["8007","Fawn brown","67492F"],["8008","Olive brown","694F2B"],["8011","Nut brown","533A29"],["8012","Red brown","5C3128"],["8014","Sepia brown","453729"],["8015","Chestnut brown","57332B"],["8016","Mahogany brown","483026"],["8017","Chocolate brown","42332E"],["8019","Grey brown","3B3736"],["8022","Black brown","201F20"],["8023","Orange brown","965D33"],["8024","Beige brown","6F543C"],["8025","Pale brown","6E5B4B"],["8028","Terra brown","4C3E30"],["8029","Pearl copper","764537"],["9001","Cream","E5E1D4"],["9002","Grey white","D4D5CD"],["9003","Signal white","EBECEA"],["9004","Signal black","2F3133"],["9005","Jet black","131516"],["9006","White aluminium","9A9D9D"],["9007","Grey aluminium","828280"],["9010","Pure white","EFEEE5"],["9011","Graphite black","25282A"],["9012","Cleanroom white","F2F1E1"],["9016","Traffic white","EFF0EB"],["9017","Traffic black","262625"],["9018","Papyrus white","C6CBC6"],["9022","Pearl light grey","818382"],["9023","Pearl dark grey","767779"]];
  var RAL_FAMS = [['1', 'Yellow', 'E6BE05'], ['2', 'Orange', 'DC5B00'], ['3', 'Red', 'AB1519'], ['4', 'Violet', '852E6F'], ['5', 'Blue', '0F518A'],
    ['6', 'Green', '3A8258'], ['7', 'Grey', '8C9190'], ['8', 'Brown', '74502F'], ['9', 'White & black', 'EFF0EB']];
  function ralInk(hex) { var n = parseInt(hex, 16), r = n >> 16, g = (n >> 8) & 255, b = n & 255; return (0.299 * r + 0.587 * g + 0.114 * b) > 150 ? '#1a1a1a' : '#fff'; }
  function ralFind(text) { var m = /RAL\s*(\d{4})/i.exec(String(text || '')); if (!m) return null; for (var i = 0; i < RAL.length; i++) if (RAL[i][0] === m[1]) return RAL[i]; return null; }

  var instances = 0;

  function shellHtml() {
    return '<div class="js-fwo-mam-mode"></div><div class="js-fwo-mam-units"></div><div class="js-fwo-mam-avpn"></div><div class="js-fwo-mam-axes"></div>' +
      '<p class="fwo-mam__photo-note js-fwo-mam-photo-note" hidden></p>' +
      '<div class="fwo-mam__summary js-fwo-mam-summary" hidden></div>' +
      '<p class="fwo-mam__missing js-fwo-mam-missing" hidden></p>';
  }

  // Pill + fieldset markup in this theme's classes — also used by quote-form.js's
  // options pop-up (FwoMamBuilder.ui) so both pop-ups look the same.
  function pill(name, value, label, on, extra) {
    return '<label class="fwo-config__option' + (on ? ' ' + THEME.selected : '') + '">' +
      '<input class="fwo-config__radio" type="radio" name="' + name + '" value="' + esc(value) + '"' + (on ? ' checked' : '') + '>' +
      '<span class="' + THEME.pillText + '">' + esc(label) + (extra ? ' <span class="fwo-mam__qty">' + esc(extra) + '</span>' : '') + '</span></label>';
  }
  function group(legendHtml, inner, desc) {
    return '<fieldset class="' + THEME.group + '"><legend class="' + THEME.legend + '">' + legendHtml + '</legend>' +
      '<div class="' + THEME.options + '">' + inner + '</div>' +
      (desc != null ? '<div class="fwo-mam__desc">' + esc(desc) + '</div>' : '') + '</fieldset>';
  }
  function create(root, data, hooks) {
    ensureStyles();
    hooks = hooks || {};
    var uid = 'fwomam' + (++instances);
    var cfg = (data && data.config) || {};
    var media = (data && data.media) || [];
    var units = (data && data.units) || [];
    var specialId = data && data.special ? String(data.special) : '';
    var axes = cfg.axes || [];
    var rules = cfg.images || [];
    // With stock on hand the customer picks a mode first; without, it's always special order.
    // With stock on hand, start on "Available now" (and pick the unit when there's just one).
    var mode = units.length ? 'stock' : 'special'; // 'stock' | 'special'
    var unitId = units.length === 1 ? String(units[0].id) : null;
    var state = {};      // axisId -> optionId (pills) | [optionIds] (multi) | string (text)
    var touched = false; // don't nag about missing choices before the first click
    var ralOpen = null, ralFam = '3', ralQ = ''; // RAL picker: open axis id, family digit, search text
    var modeEl = root.querySelector('.js-fwo-mam-mode');
    var unitsEl = root.querySelector('.js-fwo-mam-units');
    var axesEl = root.querySelector('.js-fwo-mam-axes');
    var MODE = uid + '-mode', UNIT = uid + '-unit';
    // AVPN preset (owner, 2026-10-20: "a button that auto-selects AVPN configuration"): config.avpn = { axisId: optionId }
    // from the product's custom.mam_builder metafield (Napoli / Torino: Static + Standard 54 cm door). No preset = no button.
    var avpn = cfg.avpn && typeof cfg.avpn === 'object' ? cfg.avpn : null;
    var avpnEl = root.querySelector('.js-fwo-mam-avpn');
    var avpnLogo = (data && data.avpn_logo) || '';
    // A preset value can be one option id or a list of approved ids (Roma / Milano: Traditional or Easy); the first is the default.
    function avpnIds(k) { return Array.isArray(avpn[k]) ? avpn[k] : [avpn[k]]; }
    function avpnOn() { if (!avpn) return false; for (var k in avpn) if (avpnIds(k).indexOf(state[k]) === -1) return false; return true; }

    function axisById(id) { for (var i = 0; i < axes.length; i++) if (axes[i].id === id) return axes[i]; return null; }
    function labelOf(axisId, optId) {
      var ax = axisById(axisId); if (!ax) return optId;
      for (var i = 0; i < (ax.options || []).length; i++) if (ax.options[i].id === optId) return ax.options[i].label;
      return optId;
    }
    // { axis: [ids] } is met when every listed axis currently holds one of its ids.
    function met(cond) {
      if (!cond) return true;
      for (var k in cond) {
        var v = state[k], ok = cond[k];
        if (Array.isArray(v) ? !v.some(function (x) { return ok.indexOf(x) !== -1; }) : ok.indexOf(v) === -1) return false;
      }
      return true;
    }
    function anyMet(cond) { // disable_if: ANY listed axis holding a listed id disables the option
      if (!cond) return false;
      for (var k in cond) { var o = {}; o[k] = cond[k]; if (met(o)) return true; }
      return false;
    }
    function visibleOptions(ax) { return (ax.options || []).filter(function (o) { return met(o.show_if); }); }
    function axisVisible(ax) { return met(ax.show_if) && (ax.type === 'text' || visibleOptions(ax).length > 0); }
    function optionUsable(o) { return met(o.show_if) && !anyMet(o.disable_if); }
    // required_if (2026-10-06): the axis is required only while its condition holds
    // (e.g. color when finish = spicchio). Text axes are optional unless required:true
    // or a met required_if; option axes are required unless required:false.
    function isRequired(ax) {
      if (ax.required_if) return met(ax.required_if);
      return ax.type === 'text' ? ax.required === true : ax.required !== false;
    }

    // Drop choices that a later change made invalid (e.g. a size that
    // belongs to another version), repeating until nothing else changes.
    function prune() {
      for (var pass = 0; pass < 5; pass++) {
        var changed = false;
        axes.forEach(function (ax) {
          var v = state[ax.id];
          if (v == null || v === '') return;
          if (!axisVisible(ax)) { delete state[ax.id]; changed = true; return; }
          if (ax.type === 'text') return;
          var usable = function (id) { var o = (ax.options || []).filter(function (x) { return x.id === id; })[0]; return o && optionUsable(o); };
          if (ax.type === 'multi') {
            var keep = v.filter(usable);
            if (keep.length !== v.length) { state[ax.id] = keep; changed = true; }
          } else if (!usable(v)) { delete state[ax.id]; changed = true; }
        });
        if (!changed) return;
      }
    }

    function renderStock() {
      modeEl.innerHTML = !units.length ? '' : group(T('Availability'),
        pill(MODE, 'stock', T('Available now'), mode === 'stock') +
        pill(MODE, 'special', T('Special order — build your own'), mode === 'special'),
        mode === 'stock' ? T('Finished ovens we have on hand — the fastest way to get baking.') : mode === 'special' ? T('Made to order by MAM in Modena with exactly the options you choose.') : '');
      unitsEl.innerHTML = mode !== 'stock' ? '' : group(T('Choose your oven'),
        units.map(function (u) { return pill(UNIT, String(u.id), u.title, String(u.id) === unitId); }).join(''));
      axesEl.style.display = mode === 'special' ? '' : 'none';
      if (avpnEl) {
        if (!avpn) { avpnEl.innerHTML = ''; }
        else {
          var on = mode === 'special' && avpnOn();
          avpnEl.innerHTML = '<div class="fwo-mam__avpn"><button type="button" class="fwo-mam__avpn-btn js-fwo-mam-avpn-btn' + (on ? ' is-on' : '') + '">' +
            (avpnLogo ? '<img src="' + esc(avpnLogo) + '" alt="" width="26" height="23">' : '') +
            esc(on ? T('AVPN configuration selected') + ' ✓' : T('Select the AVPN configuration')) + '</button>' +
            '<div class="fwo-mam__desc">' + esc(T(cfg.avpn_note || 'Static oven with the standard 54 cm door — the setup approved by the AVPN.')) + '</div></div>';
        }
      }
    }

    // RAL Classic picker (owner, 2026-10-21: "For the Mam builder I would like a widget for the ral selector").
    // `ral` is true or a show_if-style condition, e.g. {"finish":["spicchio"]} (owner: "should only appear when Spicchio Painted is selected").
    function ralSwatches(axId) {
      var q = ralQ.trim().toLowerCase(), cur = ralFind(state[axId]);
      var famName = function (c) { var f = RAL_FAMS.filter(function (x) { return x[0] === c[0].charAt(0); })[0]; return f ? f[1] + ' ' + T(f[1]) : ''; };   // "rojo" finds the reds
      var list = RAL.filter(function (c) { return q ? (('ral ' + c[0] + ' ' + c[1] + ' ' + famName(c)).toLowerCase().indexOf(q) !== -1) : c[0].charAt(0) === ralFam; });
      if (!list.length) return '<p class="fwo-ral__empty">' + esc(T('No RAL color matches that search.')) + '</p>';
      return list.map(function (c) {
        return '<button type="button" class="fwo-ral__sw' + (cur && cur[0] === c[0] ? ' is-on' : '') + '" data-ral-pick="' + c[0] + '" data-ral-axis="' + esc(axId) + '" style="background:#' + c[2] + ';color:' + ralInk(c[2]) + '" title="RAL ' + c[0] + ' ' + esc(T(c[1])) + '"><b>RAL ' + c[0] + '</b>' + esc(T(c[1])) + '</button>';
      }).join('');
    }
    function ralHtml(ax) {
      var cur = ralFind(state[ax.id]), open = ralOpen === ax.id;
      var chip = cur ? '<span class="fwo-ral__chip" style="background:#' + cur[2] + '"></span>' : '<span class="fwo-ral__chip"></span>';
      var label = cur ? 'RAL ' + cur[0] + ' ' + T(cur[1]) + ' · ' + T('change') : T('Choose a RAL color');
      return '<div class="fwo-ral"><button type="button" class="fwo-ral__toggle" data-ral-toggle="' + esc(ax.id) + '" aria-expanded="' + open + '">' + chip + '<span>' + esc(label) + '</span></button>' +
        (open ? '<div class="fwo-ral__panel"><input type="search" class="fwo-ral__search" data-ral-search="' + esc(ax.id) + '" placeholder="' + esc(T('Search a code or name, e.g. 3020 or red')) + '" value="' + esc(ralQ) + '">' +
          '<div class="fwo-ral__fams">' + RAL_FAMS.map(function (f) { return '<button type="button" class="fwo-ral__fam' + (!ralQ && ralFam === f[0] ? ' is-on' : '') + '" data-ral-fam="' + f[0] + '" data-ral-axis="' + esc(ax.id) + '"><i style="background:#' + f[2] + '"></i>' + esc(T(f[1])) + '</button>'; }).join('') + '</div>' +
          '<div class="fwo-ral__grid">' + ralSwatches(ax.id) + '</div>' +
          '<p class="fwo-ral__note">' + esc(T('Colors on screen are approximate — we paint to the RAL code.')) + '</p></div>' : '') + '</div>';
    }

    function render() {
      renderStock();
      if (mode === 'special') {
        axesEl.innerHTML = axes.map(function (ax) {
          if (!axisVisible(ax)) return '';
          var legend = esc(T(ax.label)) + (!isRequired(ax) ? ' <span style="' + THEME.optionalStyle + '">' + T('(optional)') + '</span>' : '');
          if (ax.type === 'text' && ax.ral_only && met(ax.ral_only)) {
            // Picker only, no text box (owner, 2026-10-21: "also remove the color box when spicchio painted is selected").
            // A typed colour from another finish isn't a RAL code, so it doesn't count here.
            if (state[ax.id] && !ralFind(state[ax.id])) state[ax.id] = '';
            return '<fieldset class="' + THEME.group + '"><legend class="' + THEME.legend + '">' + legend + '</legend>' +
              (ax.ral_help ? '<div class="fwo-mam__desc" style="margin:0">' + esc(T(ax.ral_help)) + '</div>' : '') + ralHtml(ax) + '</fieldset>';
          }
          if (ax.type === 'text') {
            return '<fieldset class="' + THEME.group + '"><legend class="' + THEME.legend + '">' + legend + '</legend>' +
              '<input class="fwo-mam__text" type="text" maxlength="80" data-axis="' + esc(ax.id) + '" placeholder="' + esc(T(ax.placeholder || '')) + '" value="' + esc(state[ax.id] || '') + '">' +
              (ax.help ? '<div class="fwo-mam__desc">' + esc(T(ax.help)) + '</div>' : '') + (ax.ral && (ax.ral === true || met(ax.ral)) ? ralHtml(ax) : '') + '</fieldset>';
          }
          var selDesc = '';
          var pills = visibleOptions(ax).map(function (o) {
            var multi = ax.type === 'multi';
            var on = multi ? (state[ax.id] || []).indexOf(o.id) !== -1 : state[ax.id] === o.id;
            var off = anyMet(o.disable_if);
            if (on && o.desc) selDesc = T(o.desc);
            return '<label class="fwo-config__option' + (on ? ' ' + THEME.selected : '') + '"' +
              (off ? ' aria-disabled="true" title="' + T('Not available with your current choices') + '"' : '') + '>' +
              '<input class="fwo-config__radio" type="' + (multi ? 'checkbox' : 'radio') + '" name="' + uid + '-' + esc(ax.id) + '" value="' + esc(o.id) + '"' +
              (on ? ' checked' : '') + (off ? ' disabled' : '') + ' data-axis="' + esc(ax.id) + '">' +
              '<span class="' + THEME.pillText + '">' + esc(T(o.label)) + '</span></label>';
          }).join('');
          return group(legend, pills, selDesc);
        }).join('');
      }
      updateSummary();
      updateImage();
      if (touched) showMissing(missing(), false);
    }

    function missing() {
      if (!mode) return ['Availability'];
      if (mode === 'stock') return unitId ? [] : ['Choose your oven'];
      return axes.filter(function (ax) {
        if (ax.type === 'multi' || !axisVisible(ax) || !isRequired(ax)) return false;
        if (ax.type === 'text') return !String(state[ax.id] || '').trim();
        return !state[ax.id];
      }).map(function (ax) { return ax.label; });
    }
    function showMissing(list, scroll) {
      var el = root.querySelector('.js-fwo-mam-missing');
      if (!list.length) { el.hidden = true; return; }
      el.textContent = T('Please choose: ') + list.map(T).join(', ') + '.';
      el.hidden = false;
      if (scroll !== false) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    function unitById(id) { return units.filter(function (u) { return String(u.id) === String(id); })[0] || null; }
    // "Version: Static | Cooking floor: Ø 120 cm | … | Accessories: Granite sill, Lower shelf"
    // tr = T for the on-screen summary; omitted (English) for the spec sent with the quote.
    function builtSpec(tr) {
      tr = tr || function (x) { return x; };
      return axes.filter(axisVisible).map(function (ax) {
        var v = state[ax.id];
        if (v == null || v === '' || (Array.isArray(v) && !v.length)) return '';
        var text = ax.type === 'text' ? String(v).trim()
          : Array.isArray(v) ? v.map(function (id) { return tr(labelOf(ax.id, id)); }).join(', ')
          : tr(labelOf(ax.id, v));
        return text ? tr(ax.label) + ': ' + text : '';
      }).filter(Boolean).join(' | ');
    }
    function spec(tr) {
      var t = tr || function (x) { return x; };
      if (mode === 'stock') { var u = unitById(unitId); return u ? t('Availability') + ': ' + t('In stock') + ' | ' + t('Oven') + ': ' + u.title : ''; }
      var built = builtSpec(tr);
      return built && units.length ? t('Availability') + ': ' + t('Special order') + ' | ' + built : built;
    }
    function updateSummary() {
      var el = root.querySelector('.js-fwo-mam-summary');
      var s = spec(T); // shown in the shopper's language; spec() (English) is what the quote carries
      el.hidden = !s;
      el.innerHTML = s ? '<strong>' + (mode === 'stock' ? T('Your oven:') : T('Your build:')) + '</strong><br>' + esc(s).split(' | ').join('<br>') : '';
    }

    // Photo: the chosen unit's variant image, or the most specific image rule
    // that fully matches the build. The host decides how to show it.
    var lastMid = null;
    function show(m) {
      if (!m || m.id === lastMid) return;
      lastMid = m.id;
      if (hooks.showMedia) hooks.showMedia(m);
    }
    function updateImage() {
      var note = root.querySelector('.js-fwo-mam-photo-note');
      if (mode !== 'special') {
        note.hidden = true;
        var u = unitById(unitId);
        if (u && u.media) show(media.filter(function (x) { return String(x.id) === String(u.media); })[0]);
        return;
      }
      var best = null, bestN = -1;
      rules.forEach(function (r) {
        var n = Object.keys(r.when || {}).length;
        var ok = Object.keys(r.when || {}).every(function (k) { return state[k] === r.when[k]; });
        if (ok && n > bestN) { best = r; bestN = n; }
      });
      if (!best || bestN === 0) {
        note.hidden = !(axisById('finish') && state.finish);
        note.textContent = T('We don’t have a photo of this exact finish yet — your MAM specialist will send samples with the quote.');
        return;
      }
      note.hidden = !best.note;
      note.textContent = best.note ? T(best.note) : '';
      show(media.filter(function (x) { return x.file && x.file.indexOf(best.file) !== -1; })[0]);
    }

    root.addEventListener('click', function (e) {
      var el = e.target && e.target.closest ? e.target.closest('[data-ral-toggle],[data-ral-fam],[data-ral-pick]') : null;
      if (!el) return;
      e.preventDefault();
      if (el.hasAttribute('data-ral-toggle')) {
        var id = el.getAttribute('data-ral-toggle');
        ralOpen = ralOpen === id ? null : id; ralQ = '';
        var cur = ralFind(state[id]); if (cur) ralFam = cur[0].charAt(0);
      } else if (el.hasAttribute('data-ral-fam')) {
        ralFam = el.getAttribute('data-ral-fam'); ralQ = '';
      } else {
        var c = ralFind('RAL ' + el.getAttribute('data-ral-pick')), axId = el.getAttribute('data-ral-axis');
        if (c) state[axId] = 'RAL ' + c[0] + ' ' + c[1];   // English name: the spec/cart text stays English
        ralOpen = null; ralQ = '';
      }
      render();
      var t = root.querySelector(ralOpen ? '[data-ral-search]' : '[data-ral-toggle]'); if (t && el.hasAttribute('data-ral-toggle')) t.focus();
    });
    root.addEventListener('input', function (e) {
      var t = e.target; if (!t || !t.hasAttribute || !t.hasAttribute('data-ral-search')) return;
      ralQ = t.value;
      var grid = t.parentNode.querySelector('.fwo-ral__grid'); if (grid) grid.innerHTML = ralSwatches(t.getAttribute('data-ral-search'));
      t.parentNode.querySelectorAll('.fwo-ral__fam').forEach(function (b) { b.classList.toggle('is-on', !ralQ && b.getAttribute('data-ral-fam') === ralFam); });
    });
    root.addEventListener('click', function (e) {
      var b = e.target && e.target.closest ? e.target.closest('.js-fwo-mam-avpn-btn') : null;
      if (!b || !avpn) return;
      e.preventDefault();
      if (mode !== 'special') { mode = 'special'; unitId = null; }
      for (var k in avpn) {
        var ax = axisById(k), ids = avpnIds(k);
        if (!ax || ids.indexOf(state[k]) !== -1) continue; // already an approved choice — keep it
        var pickId = ids.filter(function (id) { return (ax.options || []).some(function (o) { return o.id === id; }); })[0];
        if (pickId) state[k] = pickId;
      }
      prune();
      render();
    });
    root.addEventListener('change', function (e) {
      var t = e.target;
      if (!t) return;
      if (t.name === MODE) { mode = t.value; unitId = mode === 'stock' && units.length === 1 ? String(units[0].id) : null; render(); return; }
      if (t.name === UNIT) { unitId = t.value; render(); return; }
      var ax = t.getAttribute('data-axis');
      if (!ax) return;
      var def = axisById(ax);
      if (def.type === 'multi') {
        var cur = state[ax] || [];
        state[ax] = t.checked ? cur.concat([t.value]) : cur.filter(function (x) { return x !== t.value; });
      } else if (def.type !== 'text') {
        state[ax] = t.value;
      }
      prune();
      render();
    });
    root.addEventListener('input', function (e) {
      var t = e.target, ax = t && t.getAttribute('data-axis');
      if (!ax || !axisById(ax) || axisById(ax).type !== 'text') return;
      state[ax] = t.value;
      updateSummary(); // no re-render: keep focus in the text box
      if (touched) showMissing(missing(), false);
    });

    render();
    return {
      missing: function () { touched = true; return missing(); },
      showMissing: showMissing,
      spec: function () { return spec(); },
      // The variant the quote should carry: the chosen in-stock unit, or the
      // special-order variant ('' = no special-order variant found).
      variantId: function () { return mode === 'stock' ? (unitId || '') : specialId; }
    };
  }

  // ---- Oven options (non-MAM ovens) ----
  // The same questions as the product page — variant options (cascading: an option
  // only offers values that exist with the choices above it), ALFA Availability
  // (Available now = in-stock variants only), made-to-order Color, Fuel Type,
  // gas burner / base companions, Engraving — from the JSON in
  // snippets/fwo-quote-options-data.liquid. Used by the quote form's options
  // pop-up (quote-form.js → openQuoteOptions) and by the product-page picker
  // (snippets/fwo-oven-options.liquid). create() returns the same interface as the
  // MAM builder ({ missing, showMissing, spec, variantId }) plus companions() and
  // variant(), so the product page's Request a Quote handler works unchanged.
  var OO_SPECIAL = ' (special order)';

  function ooRealOptions(data) {
    var vs = data.variants || [];
    return (data.options || []).filter(function (n, i) {
      return !(vs.length === 1 && i === 0 && (vs[0].options || [])[0] === 'Default Title');
    });
  }

  // Does this product have anything to ask? (Otherwise the quote form just adds it.)
  function ooNeeded(data) {
    if (!data || !data.variants) return false;
    var vs = data.variants;
    var some = function (k) { return vs.some(function (v) { return (v[k] || []).length; }); };
    var stock = data.availability && vs.some(function (v) { return v.in_stock; });
    if ((data.colors || []).length || (data.special_colors || []).length || (data.fuels || []).length ||
        some('burners') || some('bases') || data.engraving || stock) return true;
    if (data.availability && ooRealOptions(data).length) return true; // special-order note + options
    return !!(data.oven && ooRealOptions(data).length && vs.length > 1);
  }

  var ooInstances = 0;
  function ooCreate(root, data, hooks) {
    ensureStyles();
    hooks = hooks || {};
    var name = 'fwooo' + (++ooInstances);
    var realOptions = ooRealOptions(data);
    var colors = data.colors || [], special = data.special_colors || [], fuels = data.fuels || [];
    var stockVariants = data.availability ? data.variants.filter(function (v) { return v.in_stock; }) : [];
    var askMode = stockVariants.length > 0;
    var start = null;
    for (var s = 0; s < data.variants.length; s++) if (String(data.variants[s].id) === String(hooks.variantId)) start = data.variants[s];
    start = start || data.variants[0];
    // mode: 'stock' | 'special' (null when the product has no availability step). With stock
    // on hand it starts on "Available now", landing on an in-stock variant (fixOpts below).
    var sel = { opts: (start.options || []).slice(), color: '', fuel: '', burner: 'wood', base: 'none',
      mode: askMode ? 'stock' : data.availability ? 'special' : null };
    var touched = false;

    root.innerHTML = '<div class="js-fwo-oo-groups"></div>' +
      (data.engraving ? '<fieldset class="' + THEME.group + '"><legend class="' + THEME.legend + '">' + T('Engraving') + ' <span style="' + THEME.optionalStyle + '">' + T('— optional, special order') + '</span></legend>' +
        '<div class="fwo-mam__desc" style="margin:0 0 6px">' + T('Add your pizzeria name, engraved on the oven (longer lead time).') + '</div>' +
        '<input type="text" class="fwo-mam__text js-fwo-oo-engraving" maxlength="50" autocomplete="off" placeholder="' + T('e.g. La Dolce Vita') + '"></fieldset>' : '') +
      '<p class="fwo-mam__photo-note js-fwo-oo-photo-note" hidden>' + T('No photo of this exact option yet — the photo shows the standard finish.') + '</p>' +
      '<p class="fwo-mam__missing js-fwo-oo-missing" hidden></p>';
    var noteEl = root.querySelector('.js-fwo-oo-photo-note');
    var groupsEl = root.querySelector('.js-fwo-oo-groups');
    var missingEl = root.querySelector('.js-fwo-oo-missing');

    function pool() { return sel.mode === 'stock' ? stockVariants : data.variants; }
    function matches(v, upto) {
      for (var k = 0; k < upto; k++) if ((v.options || [])[k] !== sel.opts[k]) return false;
      return true;
    }
    function currentVariant() {
      var p = pool();
      for (var i = 0; i < p.length; i++) if (matches(p[i], realOptions.length)) return p[i];
      return null;
    }
    // Option i offers only values that exist with the choices above it (e.g. QUICK 6P:
    // TOP comes only Wood-fired, so picking TOP leaves just Wood-fired under Fuel Type).
    function valuesFor(i) {
      var vals = [];
      pool().forEach(function (x) {
        if (!matches(x, i)) return;
        var val = (x.options || [])[i];
        if (val && vals.indexOf(val) < 0) vals.push(val);
      });
      return vals;
    }
    function fixOpts() {
      realOptions.forEach(function (_, i) {
        var vals = valuesFor(i);
        if (vals.length && vals.indexOf(sel.opts[i]) < 0) sel.opts[i] = vals[0];
      });
    }
    function imageFor() {
      var v = currentVariant();
      var combo = data.combo_images || {};
      var col = sel.color.replace(OO_SPECIAL, '');
      if (col) {
        var keys = [(v ? v.options : sel.opts).join('|') + '|' + col, sel.opts[0] + '|' + col, col];
        for (var i = 0; i < keys.length; i++) {
          var mid = combo[keys[i]];
          if (mid != null && data.media && data.media[String(mid)]) return { src: data.media[String(mid)], mediaId: String(mid) };
        }
      }
      if (v && v.image) return { src: v.image, mediaId: v.media_id ? String(v.media_id) : '' };
      // No photo for this exact variant: go back to the main photo (never leave the
      // previous color on screen) and say so.
      // (The note only matters when photos vary by option — not for e.g. PIZZARELLA 110V/230V.)
      var photosVary = data.variants.some(function (x) { return x.image; });
      if (data.image) return { src: data.image, mediaId: data.featured_media_id ? String(data.featured_media_id) : '', fallback: !!v && photosVary };
      return null;
    }
    function missing() {
      var out = [];
      if (askMode && !sel.mode) out.push('Availability');
      if (realOptions.length && !currentVariant()) out.push('an available combination');
      if ((colors.length || special.length) && !sel.color) out.push('Color');
      if (fuels.length && !sel.fuel) out.push('Fuel Type');
      return out;
    }
    function showMissing(list, scroll) {
      touched = true;
      var m = list || missing();
      missingEl.hidden = !m.length;
      missingEl.textContent = m.length ? T('Please choose: ') + m.map(T).join(', ') + '.' : '';
      if (m.length && scroll !== false && root.scrollIntoView) root.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    function groupsHtml() {
      var v = currentVariant();
      var html = '';
      if (askMode) {
        html += group(T('Availability'),
          pill(name + '-mode', 'stock', T('Available now'), sel.mode === 'stock') +
          pill(name + '-mode', 'special', T('Special order'), sel.mode === 'special'),
          sel.mode === 'stock' ? T('Ready to ship from our stock.') : sel.mode === 'special' ? T('Made to order — longer lead time.') : null);
      } else if (data.availability) {
        html += '<p class="fwo-mam__photo-note">' + T('Special order: made to order — longer lead time.') + '</p>';
      }
      realOptions.forEach(function (optName, i) {
        // In "Available now" the pool is in-stock units only, so just those options show (no quantities).
        html += group(esc(T(optName)), valuesFor(i).map(function (val) { return pill(name + '-o' + i, val, val, sel.opts[i] === val); }).join(''));
      });
      if (colors.length || special.length) {
        var inner = colors.map(function (c) { return pill(name + '-color', c, c, sel.color === c); }).join('');
        if (special.length) {
          inner += '<p class="fwo-mam__desc" style="flex-basis:100%;margin:6px 0 0">' + T('Special order — made to your color (longer lead time)') + '</p>' +
            special.map(function (c) { return pill(name + '-color', c + OO_SPECIAL, c, sel.color === c + OO_SPECIAL); }).join('');
        }
        html += group(T('Color'), inner);
      }
      if (fuels.length) html += group(T('Fuel Type'), fuels.map(function (f) { return pill(name + '-fuel', f, f, sel.fuel === f); }).join(''));
      var burners = (v && v.burners) || [];
      if (burners.length) {
        html += group(T('Fuel Type'), pill(name + '-burner', 'wood', T('Wood-fired'), sel.burner === 'wood') +
          burners.map(function (b) { return pill(name + '-burner', String(b.id), T('Gas/Wood Hybrid') + ' (' + b.label + ')', sel.burner === String(b.id)); }).join(''));
      }
      var bases = (v && v.bases) || [];
      if (bases.length) {
        html += group(T('Base'), pill(name + '-base', 'none', T('No base'), sel.base === 'none') +
          bases.map(function (b) { return pill(name + '-base', String(b.id), b.label, sel.base === String(b.id)); }).join(''));
      }
      return html;
    }
    function paint() {
      groupsEl.innerHTML = groupsHtml();
      var img = imageFor();
      noteEl.hidden = !(img && img.fallback);
      if (img && hooks.showImage) hooks.showImage(img, currentVariant());
      if (touched) showMissing(null, false);
    }
    groupsEl.addEventListener('change', function (e) {
      var input = e.target;
      if (!input || !input.name || input.name.indexOf(name + '-') !== 0) return;
      var key = input.name.slice(name.length + 1);
      if (key === 'mode') sel.mode = input.value;
      else if (key.charAt(0) === 'o') sel.opts[Number(key.slice(1))] = input.value;
      else if (key === 'color') sel.color = input.value;
      else if (key === 'fuel') sel.fuel = input.value;
      else if (key === 'burner') sel.burner = input.value;
      else if (key === 'base') sel.base = input.value;
      // A special-order color can't ship from stock: picking one switches Availability to
      // Special order; going back to Available now drops it (the one stocked color is re-picked).
      if (askMode && key === 'color' && sel.color.indexOf(OO_SPECIAL) > 0) sel.mode = 'special';
      if (askMode && key === 'mode' && sel.mode === 'stock' && sel.color.indexOf(OO_SPECIAL) > 0) sel.color = colors.length === 1 ? colors[0] : '';
      // And the other way: on Special order, landing on a stocked color + an in-stock combination
      // switches back to Available now (only an explicit Special order click keeps it special).
      if (askMode && key !== 'mode' && sel.mode === 'special' && sel.color.indexOf(OO_SPECIAL) < 0) {
        fixOpts();
        var sv = currentVariant();
        if (sv && sv.in_stock) sel.mode = 'stock';
      }
      fixOpts(); // keep later options valid for the new choice (and on an in-stock unit in "Available now")
      // Burners / bases belong to a variant — reset them when it changes.
      var v = currentVariant();
      if (!v || !(v.burners || []).some(function (b) { return String(b.id) === sel.burner; })) sel.burner = 'wood';
      if (!v || !(v.bases || []).some(function (b) { return String(b.id) === sel.base; })) sel.base = 'none';
      paint();
      if (hooks.onChange) hooks.onChange();
    });
    fixOpts();
    paint();

    function find(list, id) { for (var i = 0; i < list.length; i++) if (String(list[i].id) === id) return list[i]; return null; }
    return {
      missing: missing,
      showMissing: showMissing,
      variant: function () { return currentVariant(); },
      variantId: function () { var v = currentVariant(); return v ? String(v.id) : ''; },
      // Burner / base the customer picked — added to the quote as their own lines.
      companions: function () {
        var v = currentVariant() || {};
        return [find(v.burners || [], sel.burner), find(v.bases || [], sel.base)].filter(Boolean);
      },
      // One label carrying every choice ("Availability: Special order · Style: TOP · Color: Corten").
      spec: function () {
        var v = currentVariant() || {};
        var parts = [];
        if (sel.mode) parts.push('Availability: ' + (sel.mode === 'stock' ? 'In stock' : 'Special order'));
        realOptions.forEach(function (optName, i) { if (sel.opts[i]) parts.push(optName + ': ' + sel.opts[i]); });
        if (sel.color) parts.push('Color: ' + sel.color);
        if (sel.fuel) parts.push('Fuel Type: ' + sel.fuel);
        if ((v.burners || []).length) parts.push('Fuel Type: ' + (find(v.burners, sel.burner) ? 'Gas/Wood Hybrid' : 'Wood-fired'));
        var eng = root.querySelector('.js-fwo-oo-engraving');
        if (eng && eng.value.trim()) parts.push('Engraving: ' + eng.value.trim());
        return parts.join(' · ').replace(/<\/?tc>/gi, '').slice(0, 900);
      }
    };
  }

  window.FwoMamBuilder = { create: create, shellHtml: shellHtml, ensureStyles: ensureStyles, ui: { pill: pill, group: group },
    ovenOptions: { create: ooCreate, needed: ooNeeded } };
})();
